import { ConstitutionSnapshot, ShareNumberingSnapshot, SocioSnapshot } from '../models/constitution-snapshot.model';

interface NewSharesRange {
  serieId: number | null;
  serieName: string;
  from: number;
  to: number;
}

function toNumber(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = Number(String(value).replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

/**
 * Numbering range of the shares issued by an ampliación de acciones. Uses "De la / A la" when the
 * operation recorded them; otherwise derives it from the saved capital: the serie ends at its
 * `sharesNumberTo` and the new shares are the last `accNuevasAcciones` of it.
 */
function getNewSharesRanges(operationData: Record<string, unknown>): NewSharesRange[] {
  const amp = (operationData['ampliacion'] as Record<string, unknown> | undefined) ?? operationData;
  if (amp['modoAmpliacion'] !== 'ampliacion_acciones') return [];
  const capital = operationData['capitalSocial'] as { hasSeries?: boolean; sharesNumberTo?: unknown; series?: Array<Record<string, unknown>> } | undefined;
  // La serie principal más las añadidas en el mismo paso (accExtras)
  const filas = [
    { serie: amp['accSerie'], serieNombre: amp['accSerieNombre'], nuevas: amp['accNuevasAcciones'] ?? amp['accNumAmpliado'], desde: amp['accDesde'], hasta: amp['accHasta'] },
    ...(Array.isArray(amp['accExtras']) ? (amp['accExtras'] as Array<Record<string, unknown>>) : []),
  ];
  const ranges: NewSharesRange[] = [];
  for (const fila of filas) {
    const serieId = toNumber(fila['serie']);
    const serieName = String(fila['serieNombre'] ?? '').trim().toLowerCase();
    let from = toNumber(fila['desde']);
    let to = toNumber(fila['hasta']);
    if (from == null || to == null) {
      const count = toNumber(fila['nuevas']);
      const serie = (capital?.series ?? []).find(s =>
        (serieId != null && (toNumber(s['id']) === serieId || toNumber(s['feLocalId']) === serieId))
        || (serieName !== '' && String(s['name'] ?? '').trim().toLowerCase() === serieName));
      // Sin series el capital es único: las nuevas acciones son las últimas de su numeración.
      const serieTo = toNumber(serie?.['sharesNumberTo'] ?? (capital?.hasSeries ? null : capital?.sharesNumberTo));
      if (count == null || count <= 0 || serieTo == null) continue;
      to = serieTo;
      from = serieTo - count + 1;
    }
    if (to >= from) ranges.push({ serieId, serieName, from, to });
  }
  return ranges;
}

function sameSerie(n: ShareNumberingSnapshot, range: NewSharesRange): boolean {
  if (range.serieId != null && n.seriesId != null && n.seriesId === range.serieId) return true;
  if (range.serieName && n.seriesName) return n.seriesName.trim().toLowerCase() === range.serieName;
  return range.serieId == null || n.seriesId == null;
}

/**
 * For an ampliación, keeps in each partner only the newly issued shares (clipping numberings to the
 * new range) and drops partners that received none. Returns the snapshot unchanged when the
 * operation has no recorded new range.
 */
export function restrictSociosToNewShares(
  snapshot: ConstitutionSnapshot,
  operationData: Record<string, unknown> | null | undefined,
): ConstitutionSnapshot {
  const ranges = operationData ? getNewSharesRanges(operationData) : [];
  if (!Array.isArray(snapshot.socios)) return snapshot;
  if (ranges.length === 0) return restrictSociosToVnSerie(snapshot, operationData);

  const socios: SocioSnapshot[] = [];
  for (const socio of snapshot.socios) {
    const numberings: ShareNumberingSnapshot[] = [];
    for (const n of socio.shareNumberings ?? []) {
      for (const range of ranges) {
        if (!sameSerie(n, range)) continue;
        const from = Math.max(n.sharesNumberFrom, range.from);
        const to = Math.min(n.sharesNumberTo, range.to);
        if (to < from) continue;
        numberings.push({ ...n, sharesNumberFrom: from, sharesNumberTo: to, sharesCount: to - from + 1 });
      }
    }
    if (numberings.length === 0) continue;
    socios.push({
      ...socio,
      shareNumberings: numberings,
      numAcciones: numberings.reduce((sum, n) => sum + n.sharesCount, 0),
    });
  }
  return { ...snapshot, socios };
}

/**
 * For an ampliación with series, keeps in the capital table only the serie the operation touches
 * (the "Ampliación VN" or "Ampliación acciones" one). Unchanged for a capital without series or when
 * the serie cannot be identified.
 */
export function restrictCapitalToAffectedSeries(
  snapshot: ConstitutionSnapshot,
  operationData: Record<string, unknown> | null | undefined,
): ConstitutionSnapshot {
  const capital = snapshot.capitalSocial;
  if (!operationData || !capital?.hasSeries || !Array.isArray(capital.series)) return snapshot;
  const amp = (operationData['ampliacion'] as Record<string, unknown> | undefined) ?? operationData;
  const isVn = amp['modoAmpliacion'] === 'ampliacion_vn';
  if (!isVn && amp['modoAmpliacion'] !== 'ampliacion_acciones') return snapshot;

  const targets = isVn ? vnTargets(amp) : accTargets(amp);
  const affected = capital.series.filter(s => targets.some(({ serieId, serieName }) =>
    (serieName !== '' && String(s.nombre ?? '').trim().toLowerCase() === serieName)
    || (serieId != null && s.id === serieId)));
  return affected.length === 0 ? snapshot : { ...snapshot, capitalSocial: { ...capital, series: affected } };
}

/** Series que toca una ampliación de acciones: la principal más las añadidas (`accExtras`). */
function accTargets(amp: Record<string, unknown>): Array<{ serieId: number | null; serieName: string }> {
  const extras = Array.isArray(amp['accExtras']) ? (amp['accExtras'] as Array<Record<string, unknown>>) : [];
  return [
    { serieId: toNumber(amp['accSerie']), serieName: String(amp['accSerieNombre'] ?? '').trim().toLowerCase() },
    ...extras.map(e => ({ serieId: toNumber(e['serie']), serieName: String(e['serieNombre'] ?? '').trim().toLowerCase() })),
  ];
}

/** Series que toca una ampliación de VN: la principal más las añadidas (`vnExtras`). */
function vnTargets(amp: Record<string, unknown>): Array<{ serieId: number | null; serieName: string }> {
  const extras = Array.isArray(amp['vnExtras']) ? (amp['vnExtras'] as Array<Record<string, unknown>>) : [];
  return [
    { serieId: toNumber(amp['vnSerie']), serieName: String(amp['vnSerieNombre'] ?? '').trim().toLowerCase() },
    ...extras.map(e => ({ serieId: toNumber(e['serie']), serieName: String(e['serieNombre'] ?? '').trim().toLowerCase() })),
  ];
}

/** Ampliación de VN: los socios se reducen a lo que tienen en la serie modificada. */
function restrictSociosToVnSerie(
  snapshot: ConstitutionSnapshot,
  operationData: Record<string, unknown> | null | undefined,
): ConstitutionSnapshot {
  if (!operationData) return snapshot;
  const amp = (operationData['ampliacion'] as Record<string, unknown> | undefined) ?? operationData;
  if (amp['modoAmpliacion'] !== 'ampliacion_vn') return snapshot;
  const ranges: NewSharesRange[] = vnTargets(amp)
    .filter(t => t.serieId != null || t.serieName !== '')
    .map(t => ({ ...t, from: 0, to: Number.MAX_SAFE_INTEGER }));
  if (ranges.length === 0) return snapshot;

  const socios = snapshot.socios.flatMap(socio => {
    const numberings = (socio.shareNumberings ?? []).filter(n => ranges.some(range => sameSerie(n, range)));
    return numberings.length === 0 ? [] : [{
      ...socio,
      shareNumberings: numberings,
      numAcciones: numberings.reduce((sum, n) => sum + n.sharesCount, 0),
    }];
  });
  return { ...snapshot, socios };
}
